import { useState, useEffect, useRef } from "react";
import { FeatureEnabledType, TimelineStateType } from "src/shared/types/src";
import ActivityFilterItem from "./ActivityFilterItem";
import { TimelineActions } from "src/shared/redux-state/src/lib/timeline/TimelineSlice";
import { dateFormatter } from "src/shared/utils/src";
import { ACTIVITY_TYPE, DATE_FORMATS } from "src/shared/config/src";
import LocalImages from './../../../../static';
import { useAppDispatch, useAppSelector } from "src/app/services/store";
import { ActivityFilterList, URL_CONSTANTS } from "src/app/config/appConstants";
import { useNavigate } from 'react-router-dom'
import { canViewMedicineFormsSelector, selectFeaturesEnabled } from "src/shared/redux-state/src";
import Slider from "react-slick";
import { LiaAngleLeftSolid } from "react-icons/lia";
import { LiaAngleRightSolid } from "react-icons/lia";
import { getQueryString } from "src/app/utilities/helpers";

type QuickFilterProps = {
  timelineFilters: TimelineStateType['filters']
}

const QuickFilters = ({ timelineFilters }: QuickFilterProps) => {

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const canViewMedicineForms: boolean = useAppSelector(canViewMedicineFormsSelector);
  const featuresEnabled: FeatureEnabledType = useAppSelector(selectFeaturesEnabled);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(true);


  const handleQuickFilterChange = async (activityType: string, appliedFilters: TimelineStateType['filters']) => {
    try {
      let isFilterApplied = false;
      let selectedActivityTypes = JSON.parse(JSON.stringify(appliedFilters?.activityTypes)) || [];
      if (selectedActivityTypes.includes(activityType)) {
        selectedActivityTypes = selectedActivityTypes.filter((x: string) => x !== activityType);
      } else {
        selectedActivityTypes = [...selectedActivityTypes, activityType];
      }

      const selectedFromDate = appliedFilters.from;
      const selectedToDate = appliedFilters.to;
      const showFutureEvents = appliedFilters.showFutureEvents;

      if (selectedActivityTypes?.length !== 0 || selectedFromDate || selectedToDate || showFutureEvents) {
        isFilterApplied = true;
      }

      await dispatch(TimelineActions.updateFilters({
        selectedTypes: selectedActivityTypes,
        from: dateFormatter(selectedFromDate, DATE_FORMATS.YMD),
        to: dateFormatter(selectedToDate, DATE_FORMATS.YMD),
        isFilterApplied,
        showFutureEvents
      }));

      const query = getQueryString({
        from: selectedFromDate,
        to: selectedToDate,
        activityType: selectedActivityTypes,
        showFutureEvents
      });
      
      if(query){
        navigate(`?${query}`, {replace: true})
      }else{
        navigate('', {replace: true})
      }

    } catch (error) {
      console.log("error while applying quick filters: ", error);
    }
  }

  const _renderActivityFilterItem = (item: {key: string, label: string, iconName: keyof typeof LocalImages}, index: number) => {
    if((!canViewMedicineForms && item.key === ACTIVITY_TYPE.MEDICINE_FORM) || (!featuresEnabled?.accidentSheet && item.key === ACTIVITY_TYPE.ACCIDENT_SHEET)){
      return
    } else {
      return (
        <div key={index} className="quick-filter-item">
          <ActivityFilterItem
            key={item.key}
            label={item.label}
            iconName={item.iconName}
            onPress={() => handleQuickFilterChange(item.key, Object.assign({}, timelineFilters))}
            isSelected={timelineFilters?.activityTypes?.includes(item.key) || false}
          />
        </div>
      )
    }
  };

  const checkScrollPosition = () => {
    const el = scrollRef.current;
    if (!el) return;

    const atStart = el.scrollLeft === 0;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1; // 1px extra spacing

    setShowLeft(!atStart);
    setShowRight(!atEnd);
  };

  const scroll = (direction: 'left' | 'right') => {
    const scrollAmount = 200;
    scrollRef.current?.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    checkScrollPosition();
    el.addEventListener('scroll', checkScrollPosition);
    return () => el.removeEventListener('scroll', checkScrollPosition);
  }, []);


  return (
    <div className="mg-t-5">
      <div className="quick-filters-wrapper">
        {showLeft && <button className="slick-arrow slick-prev" onClick={() => scroll('left')}><LiaAngleLeftSolid color="#000" size={12} /></button> }
        <div className="quick-filters" ref={scrollRef}>
          {ActivityFilterList.map((item, index) => _renderActivityFilterItem(item, index))}
        </div>
        {showRight && <button className="slick-arrow slick-next" onClick={() => scroll('right')}><LiaAngleRightSolid color="#000" size={12} /></button> }
      </div>
    </div>
  )

}

export default QuickFilters;